import axios from "axios";

// Prefer same-origin so the frontend always calls the API on the host it was
// served from. The Emergent ingress routes `/api/*` on any host to the backend
// pod, so `<current-origin>/api` works on preview, production, and any custom
// domain — while eliminating cross-origin CORS/preflight-redirect failures
// that occur when REACT_APP_BACKEND_URL points at a *different* host than
// where the SPA is actually loaded (e.g., production served from
// `*.emergent.host` but env baked with a custom domain).
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BROWSER_ORIGIN =
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : null;
const BACKEND_URL = BROWSER_ORIGIN || ENV_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
});

/**
 * Extracts a user-friendly error string from either a raw Axios error OR the
 * `detail` field that FastAPI puts on HTTP error responses.
 *
 * Callers historically used the pattern:
 *   toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Custom fallback");
 *
 * Returns `null` (falsy) when there is nothing meaningful to show so the
 * caller's contextual fallback ("Failed to place order", etc.) is displayed
 * instead of a generic "Something went wrong" message.
 */
export function formatApiErrorDetail(errOrDetail) {
  if (errOrDetail == null) return null;

  // Handle a full Axios error object.
  if (errOrDetail.isAxiosError || errOrDetail.response || errOrDetail.request) {
    const detail = errOrDetail.response?.data?.detail;
    if (detail != null) return formatApiErrorDetail(detail);

    const status = errOrDetail.response?.status;
    if (status === 401) return "Invalid email or password";
    if (status === 403) return "You don't have permission for this action.";
    if (status === 404) return "Not found.";
    if (status === 429) return "Too many attempts. Please wait a moment.";
    if (status && status >= 500) return `Server error (${status}). Please try again in a moment.`;
    if (!errOrDetail.response || errOrDetail.code === "ERR_NETWORK") {
      return "Cannot reach server. Check your internet and try again.";
    }
    return errOrDetail.message || null;
  }

  if (typeof errOrDetail === "string") return errOrDetail;

  if (Array.isArray(errOrDetail)) {
    const joined = errOrDetail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
    return joined || null;
  }

  if (errOrDetail && typeof errOrDetail.msg === "string") return errOrDetail.msg;

  return String(errOrDetail);
}
